import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { hideModal, setShowGuide } from '../../../store/reducers/AppSlice'
import { useAppDispatch } from '../../../store/hooks/Redux'
import { IconArrowUp } from '../../../../shared/components/icons/IconArrowUp'
import { Link } from 'react-router-dom'

export const GuideModal = () => {
  const dispatch = useAppDispatch()

  return (
    <div className={'max-h-full overflow-y-auto py-[40px]'}>
      <h2 className={'text-center text-[36px]'}>Quick tour</h2>
      <div className={'px-[60px]'}>
        <BpTabs align={'center'} className='mt-[19px]' size={'xs'}>
          <BpTab title={'Create a project'}>
            <div className={'flex flex-col gap-[60px] py-[24px]'}>
              <div className={'flex flex-col gap-[16px]'}>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Below you will find a detailed guide on how to create and edit projects in the
                  system. This guide will help you set up monitoring on your own, configure the
                  necessary parameters, and receive useful analytics about your brand, competitors,
                  and partners—without the help of an account manager.
                </p>
              </div>

              <div className={'flex flex-col gap-[24px]'}>
                <h2 className={'text-[36px]'}>
                  1. Creating a{' '}
                  <Link
                    className={'font-medium text-default'}
                    to={'/projects'}
                    onClick={() => dispatch(hideModal())}
                  >
                    New Project
                  </Link>
                </h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  To start using the system and launch monitoring, you first need to create a new
                  project. To do this, go to the page{' '}
                  <Link
                    className={'text-default'}
                    to={'/projects'}
                    onClick={() => dispatch(hideModal())}
                  >
                    Projects
                  </Link>{' '}
                  and click <strong>Add Project</strong>.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>Step 1. Filling Out General Project Information</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>Project Name:</strong> Fill in this field to make it easier to find the
                  project in lists and filters on reporting pages.
                  <br />
                  Example: “Bluepear UK, US”
                  <br />
                  <strong>Brand Name:</strong> Specify your brand name so that the system can
                  correctly track mentions by partners, competitors, and in search engine results.
                  <br />
                  Example: “Bluepear”.
                  <br />
                  <strong>Brand’s Misspellings/Variations:</strong> Include all possible typos,
                  alternative or shortened names of the brand. This helps the system find any
                  inaccurate mentions.
                  <br />
                  <strong>Domains:</strong> Enter the domains belonging to your brand for correct
                  monitoring of violations.
                  <br />
                  <strong>Search Engines:</strong> Select the search engines in which the checks
                  will be performed.
                  <br />
                  <strong>Devices:</strong> Indicate on which types of devices (desktop, mobile)
                  searches will be conducted.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  After completing all fields, click <strong>Next</strong>. If any required fields
                  are missing, the button will be inactive.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>Step 2. Setting Up Location</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>Country:</strong> Specify the country where the search and monitoring of
                  results will be conducted. You can add more countries later in the project
                  settings.
                  <br />
                  <strong>Cities:</strong> If regional targeting is important to you, list the
                  specific cities in the chosen country.
                  <br />
                  <strong>Browser Language:</strong> Specify the language to be emulated in search
                  results (for example, English).
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Click <strong>Next</strong> to proceed to the next step.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>Step 3. Keywords</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Add the keywords for which the system will scan search results. When all the
                  necessary queries have been entered, click <strong>Create Project</strong>.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  If you encounter any errors or have questions when creating the project, please{' '}
                  <span
                    className={'cursor-pointer text-default hover:text-[#40a9ff]'}
                    onClick={() => {
                      dispatch(hideModal())
                      dispatch(setShowGuide())
                    }}
                  >
                    contact support
                  </span>
                </p>
              </div>

              <div className={'flex flex-col gap-[24px]'}>
                <h2 className={'text-[36px]'}>2. Editing an Existing Project</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  After creating a project, you can always go back to its settings and make changes.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  To edit an existing project:
                  <ul className={'list-decimal pl-[36px]'}>
                    <li>
                      Go to{' '}
                      <Link
                        className={'text-default'}
                        to={'/projects'}
                        onClick={() => dispatch(hideModal())}
                      >
                        <strong>Projects</strong>
                      </Link>{' '}
                      and find the project you need.
                    </li>
                    <li>
                      Click <strong>Edit</strong>.
                    </li>
                  </ul>
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Below is a brief overview of the main sections.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>2.1. General Settings</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  In this section, you can modify:
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>Project Name</strong> — if you need to give the project a new name or
                  clarify the existing one.
                  <br />
                  <strong>Brand Name</strong> — if you need to rename the brand or correct its
                  spelling.
                  <br />
                  <strong>Devices</strong> — choose which device types (desktop or mobile) should be
                  monitored for mentions.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  You can also configure Notifications and Reports here:
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>Notify about new affiliates</strong> Enable this option to receive alerts
                  whenever new affiliate violators appear.
                  <br />
                  <strong>Send full weekly report</strong> Enable this to automatically receive
                  reports about detected ads (competitors, affiliates).
                  <br />
                  <strong>Telegram Chat ID</strong> Enter the chat ID to receive notifications in
                  Telegram.
                  <br />
                  <strong>Recipients’ Email Addresses</strong> List addresses separated by commas to
                  automatically send reports by email.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  If necessary, you can immediately send a 7-day report by email or Telegram using
                  the respective buttons.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Click <strong>SAVE CHANGES</strong> to apply any modifications.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>2.2. Countries</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  This section displays the selected countries along with all details (cities,
                  language, search engines).
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <ul className={'list-disc pl-[36px]'}>
                    <li>
                      Click <strong>Add location</strong> to add a new country (or multiple
                      countries) with additional settings.
                    </li>
                    <li>
                      Remove any locations that are no longer relevant.
                      <br />
                      <strong>Important</strong>: You cannot delete the last remaining location, but
                      you can remove all others if needed.
                    </li>
                    <li>
                      Click <strong>SAVE CHANGES</strong> after making all necessary updates.
                    </li>
                  </ul>
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>2.3. Keywords</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Here, you can modify or expand the list of search queries.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <ul className={'list-disc pl-[36px]'}>
                    <li>Add new keywords if needed, or remove outdated ones.</li>
                    <li>
                      Click <strong>SAVE CHANGES</strong> to apply the changes.
                    </li>
                  </ul>
                </p>
              </div>

              <div className={'flex flex-col gap-[24px]'}>
                <h2 className={'text-[36px]'}>3. Useful Tips</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <ul className={'list-disc pl-[36px]'}>
                    <li>
                      If the <strong>Next</strong>, <strong>Create Project</strong>, or{' '}
                      <strong>SAVE CHANGES</strong> button is inactive, check whether all required
                      fields are filled in.
                    </li>
                    <li>Use the hints and placeholders — they indicate the correct data format.</li>
                    <li>
                      Notifications in Telegram and via email will only work if you have specified a{' '}
                      <strong>Telegram Chat ID</strong> and/or{' '}
                      <strong>Recipients’ Email Addresses</strong>.
                    </li>
                    <li>
                      You can always add more locations and search queries after creating a project
                      in the <strong>Countries</strong> and <strong>Search Terms</strong> sections.
                    </li>
                    <li>
                      If you run into any problems or something is unclear,{' '}
                      <span
                        className={'cursor-pointer text-default hover:text-[#40a9ff]'}
                        onClick={() => {
                          dispatch(hideModal())
                          dispatch(setShowGuide())
                        }}
                      >
                        contact support
                      </span>
                      .
                    </li>
                  </ul>
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  By following this guide, you can quickly configure monitoring, stay informed about
                  any detected violations, and receive all the analytics you need in a timely
                  manner.
                </p>
              </div>
            </div>
          </BpTab>
          <BpTab title={'Invite Users'}>
            <div className={'flex flex-col gap-[60px] py-[24px]'}>
              <div className={'flex flex-col gap-[24px]'}>
                <h2 className={'text-[36px]'}>
                  User Management in the{' '}
                  <Link
                    className={'font-medium text-default'}
                    to={'/team'}
                    onClick={() => dispatch(hideModal())}
                  >
                    “Team”
                  </Link>{' '}
                  Section
                </h2>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>1. User Roles</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Bluepear has four roles with different levels of access:
                  <ul className={'list-disc pl-[36px]'}>
                    <li>
                      <strong>Owner</strong> — full access to the workspace
                    </li>
                    <li>
                      <strong>Administrator</strong> — can manage users, projects, and pricing plans
                    </li>
                    <li>
                      <strong>Editor</strong> — can manage projects but not users or pricing
                    </li>
                    <li>
                      <strong>User</strong> — can access assigned projects only
                    </li>
                  </ul>
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>2. User List</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Available to <strong>Owners</strong> and <strong>Administrators</strong>.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  View all users in your workspace, including:
                  <ul className={'list-disc pl-[36px]'}>
                    <li>Name</li>
                    <li>Email</li>
                    <li>Status (Active, Pending, Deactivated)</li>
                    <li>Role</li>
                    <li>Assigned Projects</li>
                    <li>Last Login</li>
                  </ul>
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Statuses:
                  <ul className={'list-disc pl-[36px]'}>
                    <li>
                      <strong>Active</strong> – has access
                    </li>
                    <li>
                      <strong>Pending</strong> – invited but hasn’t joined yet
                    </li>
                    <li>
                      <strong>Archived</strong> – no longer has access
                    </li>
                  </ul>
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>3. Invite a User</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  To add a new team member:
                  <ul className={'list-decimal pl-[36px]'}>
                    <li>
                      Click <strong>“Invite Teammates”</strong>.
                    </li>
                    <li>
                      Enter:
                      <ul className={'list-disc pl-[36px]'}>
                        <li>
                          <strong>Name</strong>
                        </li>
                        <li>
                          <strong>Email</strong>
                        </li>
                        <li>
                          <strong>Role</strong>
                        </li>
                        <li>
                          <strong>Projects</strong>
                        </li>
                      </ul>
                      <p>
                        Users must be assigned at least one project.
                        <br />
                        Editors and Administrators get access to all projects.
                        <br />
                        (Optional) Enable{' '}
                        <strong>“Automatically Assign to All New Projects”</strong> to include
                        future projects.
                      </p>
                    </li>
                    <li>
                      Click <strong>“Send Invitation”</strong>.
                      <p>
                        The user will receive an email to set a password.
                        <br />
                        Status remains <strong>Pending</strong> until they join.
                      </p>
                    </li>
                  </ul>
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>4. Edit a User</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  Click <strong>“Edit”</strong> next to a user (not available for Owners).
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  You can update:
                  <ul className={'list-disc pl-[36px]'}>
                    <li>Name</li>
                    <li>Role</li>
                    <li>Assigned Projects</li>
                    <li>Automatically assign to new projects</li>
                  </ul>
                  Email cannot be changed.
                  <br />
                  Click <strong>“Send invitation”</strong> to save changes.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>5. Deactivate or Restore</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  If you need to temporarily disable or restore a user:
                  <ul className={'list-disc pl-[36px]'}>
                    <li>
                      <strong>Deactivate</strong> – removes access and prevents login
                    </li>
                    <li>
                      <strong>Restore</strong> – restores access
                    </li>
                  </ul>
                  Owners cannot deactivate themselves.
                  <br />
                  For <strong>Pending</strong> users, use <strong>“Resend Invitation”</strong> to
                  send a new invite.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>6. Activate Account</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  For a new user to access the system:
                  <ul className={'list-decimal pl-[36px]'}>
                    <li>Open the email invitation.</li>
                    <li>Click the link.</li>
                    <li>Set a password.</li>
                  </ul>
                  Status changes to <strong>Active</strong> after activation.
                  <br />
                  If the link is invalid or expired (72 hours), an error message will appear.
                </p>
              </div>

              <div className={'flex flex-col gap-[16px]'}>
                <h2 className={'text-[24px]'}>7. Frequently Asked Questions (FAQ)</h2>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>Can the Owner role be transferred to another user?</strong>
                  <br />
                  No, ownership can only be transferred manually through{' '}
                  <span
                    className={'cursor-pointer text-default hover:text-[#40a9ff]'}
                    onClick={() => {
                      dispatch(hideModal())
                      dispatch(setShowGuide())
                    }}
                  >
                    customer support
                  </span>{' '}
                  (if permitted by company policy).
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>What if an invitation expires?</strong>
                  <br />
                  Click “Resend Invitation”, and the system will send a new email.
                </p>
                <p className={'text-[20px] text-focus-500 opacity-80'}>
                  <strong>How can I add a user to more projects after activation?</strong>
                  <br />
                  Click “Edit”, add the projects, and save the changes.
                </p>
              </div>
            </div>
          </BpTab>
        </BpTabs>
        <div className={'flex justify-center gap-[16px] rounded-[16px] bg-[#F7F9FB] py-[32px]'}>
          <BpButton
            className={'uppercase'}
            iconRight={<IconArrowUp />}
            size={'md'}
            onClick={() => {
              document.getElementById('guide-modal')?.scrollTo({
                top: 0,
                behavior: 'smooth',
              })
            }}
          >
            Back to top
          </BpButton>
          <BpButton
            className={'uppercase'}
            size={'md'}
            theme={'outline'}
            onClick={() => dispatch(hideModal())}
          >
            Close
          </BpButton>
        </div>
      </div>
    </div>
  )
}
